// App.js
import React from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import InventoryPage from './pages/InventoryPage';
import VendorsPage from './pages/VendorsPage';
import CustomersPage from './pages/CustomersPage';
import PaymentsPage from './pages/PaymentsPage';
import OrdersPage from './pages/OrdersPage';
import ServicesPage from './pages/ServicesPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import AuthRedirector from './context/AuthRedirector';
import AdminEmployeesPanel from './pages/EmployeesDashboard';
import TimesheetPage from './pages/TimeSheetPage';        // ✅ corrected import/casing
import AdminFormsPage from './pages/AdminFormsPage';
import InvoicesPage from './pages/InvoicesPage';
import LocationsPage from './pages/LocationsPage';
import TransfersPage from './pages/TransfersPage';
import EmailSettingsPage from './pages/EmailSettingsPage';
import RepairOrdersPage from './pages/RepairOrdersPage';
import CustomerReturnsPage from './pages/CustomerReturnsPage';
import SalesPage from './pages/SalesPage';
import ServiceRoomPage from './pages/ServiceRoomPage';
import { hasPageAuthority } from './accessControl';

const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));

const ProtectedRoute = ({ children, requiredRole, pageKey, minimumAuthority = 'read' }) => {
  const { user, loading } = useAuth();

  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword) return <Navigate to="/login" replace />;

  if (pageKey && !hasPageAuthority(user, pageKey, minimumAuthority)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredRole && !pageKey) {
    const required = new Set(
      requiredRole
        .split(',')
        .map(r => r.trim().toLowerCase())
        .filter(Boolean)
    );
    const userRole = String(user.role || '').toLowerCase();
    if (!required.has(userRole)) return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AuthRedirector />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reports" element={<ProtectedRoute pageKey="reports"><React.Suspense fallback={<div>Loading reports...</div>}><ReportsPage /></React.Suspense></ProtectedRoute>} />

          {/* Employees — merged into ONE route. Pick the roles you actually want. */}
          <Route
            path="/employees"
            element={
              <ProtectedRoute pageKey="employees">
                <AdminEmployeesPanel />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute pageKey="dashboard">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/inventory"
            element={
              <ProtectedRoute pageKey="inventory">
                <InventoryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendors"
            element={
              <ProtectedRoute pageKey="vendors">
                <VendorsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customers"
            element={
              <ProtectedRoute pageKey="customers">
                <CustomersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payments"
            element={<ProtectedRoute pageKey="payments"><PaymentsPage /></ProtectedRoute>}
          />

          <Route
            path="/invoices"
            element={
              <ProtectedRoute pageKey="invoices">
                <InvoicesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/locations"
            element={
              <ProtectedRoute pageKey="locations">
                <LocationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/transfers"
            element={
              <ProtectedRoute pageKey="transfers">
                <TransfersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/repair-orders"
            element={
              <ProtectedRoute pageKey="repair-orders">
                <RepairOrdersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer-returns"
            element={
              <ProtectedRoute pageKey="customer-returns">
                <CustomerReturnsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/sales"
            element={
              <ProtectedRoute pageKey="sales">
                <SalesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/service-room"
            element={
              <ProtectedRoute pageKey="service-room">
                <ServiceRoomPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders"
            element={
              <ProtectedRoute pageKey="orders">
                <OrdersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/services"
            element={
              <ProtectedRoute pageKey="services">
                <ServicesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/timesheets"
            element={
              <ProtectedRoute pageKey="timesheets">
                <TimesheetPage /> {/* ✅ corrected component name */}
              </ProtectedRoute>
            }
          />

          <Route
            path="/forms"
            element={
              <ProtectedRoute pageKey="forms">
                <AdminFormsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/email-settings"
            element={
              <ProtectedRoute pageKey="email-settings">
                <EmailSettingsPage />
              </ProtectedRoute>
            }
          />

          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
